import { useState, useEffect } from 'react';
import api from '../../../api/client';

const EMPTY_FORM = { fromUserId: '', toUserId: '', scope: '', startsAt: '', endsAt: '' };

// datetime-local yields "YYYY-MM-DDTHH:mm"; the API expects a full ISO datetime.
function toIso(local) {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? local : d.toISOString();
}

export default function DelegationsPage() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/bp/delegations');
      setItems(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function patch(key, val) { setForm((f) => ({ ...f, [key]: val })); }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        fromUserId: form.fromUserId,
        toUserId:   form.toUserId,
        startsAt:   toIso(form.startsAt),
        endsAt:     toIso(form.endsAt),
      };
      if (form.scope) body.scope = form.scope;
      await api.post('/bp/delegations', body);
      setForm(EMPTY_FORM);
      setAddOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="bp-delegations-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Delegations</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div style={{ marginBottom: '1rem' }}>
        <button data-testid="bp-delegation-add" onClick={() => setAddOpen(true)}>
          Add Delegation
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>From</th>
              <th>To</th>
              <th>Scope</th>
              <th>Starts</th>
              <th>Ends</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} data-testid={`bp-delegation-row-${d.id}`}>
                <td>{d.from_user_id}</td>
                <td>{d.to_user_id}</td>
                <td>{d.scope || '—'}</td>
                <td>{d.starts_at?.slice(0, 16)}</td>
                <td>{d.ends_at?.slice(0, 16)}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5}>No delegations yet.</td></tr>}
          </tbody>
        </table>
      )}

      {addOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 380 }}>
            <h3>Add Delegation</h3>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  placeholder="From User ID"
                  value={form.fromUserId}
                  onChange={(e) => patch('fromUserId', e.target.value)}
                  required
                />
                <input
                  placeholder="To User ID"
                  value={form.toUserId}
                  onChange={(e) => patch('toUserId', e.target.value)}
                  required
                />
                <input
                  placeholder="Scope (resource type, optional)"
                  value={form.scope}
                  onChange={(e) => patch('scope', e.target.value)}
                />
                <label>
                  Starts At
                  <input
                    type="datetime-local"
                    value={form.startsAt}
                    onChange={(e) => patch('startsAt', e.target.value)}
                    required
                  />
                </label>
                <label>
                  Ends At
                  <input
                    type="datetime-local"
                    value={form.endsAt}
                    onChange={(e) => patch('endsAt', e.target.value)}
                    required
                  />
                </label>
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="bp-delegation-form-submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Add Delegation'}
                </button>
                <button type="button" onClick={() => { setAddOpen(false); setError(null); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
